export type SowFrequencySelection<TFrequency extends string = string> = {
  sowId: number;
  frequency: TFrequency;
};

export const upsertSowFrequency = <TFrequency extends string>(
  sowFrequencies: SowFrequencySelection<TFrequency>[],
  sowId: number,
  frequency: TFrequency
): SowFrequencySelection<TFrequency>[] => {
  return [
    ...sowFrequencies.filter((item) => Number(item.sowId) !== Number(sowId)),
    { sowId, frequency },
  ];
};

export const getSowFrequencyValue = (
  sowFrequencies: SowFrequencySelection[],
  sowId: number
): string | undefined => {
  return sowFrequencies
    .filter((item) => Number(item.sowId) === Number(sowId))
    .at(-1)?.frequency;
};

export const sanitizeSowFrequencies = <TFrequency extends string>(
  sowFrequencies: SowFrequencySelection<TFrequency>[],
  selectedScopeOfWorkIds: number[]
): SowFrequencySelection<TFrequency>[] => {
  const selectedIds = new Set(selectedScopeOfWorkIds.map(Number));
  const latestSelectedFrequency = new Map<number, TFrequency>();

  sowFrequencies.forEach((item) => {
    const sowId = Number(item.sowId);
    if (selectedIds.has(sowId)) {
      latestSelectedFrequency.set(sowId, item.frequency);
    }
  });

  return selectedScopeOfWorkIds
    .map((sowId) => {
      const frequency = latestSelectedFrequency.get(Number(sowId));
      return frequency ? { sowId, frequency } : null;
    })
    .filter((item): item is SowFrequencySelection<TFrequency> => item !== null);
};

/**
 * An uploaded ("existing signed") contract only needs the document, the date it
 * was signed, and the project type - the operational detail lives in the paper
 * contract. The API still requires these columns, so fill the ones the user was
 * never asked for. Values the user did enter are always kept, including a
 * deliberate zero.
 */
export const applyPreSignedDefaults = <T extends Record<string, any>>(
  values: T
): T => {
  const fallback = <V>(current: V | null | undefined, value: V): V =>
    current === undefined || current === null || current === "" ? value : current;

  const fallbackList = (current: number[] | null | undefined): number[] =>
    Array.isArray(current) ? current : [];

  return {
    ...values,
    amount: fallback(values.amount, 0),
    cancellation: fallback(values.cancellation, 0),
    estimatedSQFT: fallback(values.estimatedSQFT, 0),
    compensation: fallback(values.compensation, "N/A"),
    typeOfSpace: fallback(values.typeOfSpace, "N/A"),
    pets: fallback(values.pets, "NA"),
    recurrence: fallback(values.recurrence, "NA"),
    typeOfFloorSurfaceIds: fallbackList(values.typeOfFloorSurfaceIds),
    suppliesIds: fallbackList(values.suppliesIds),
    paperProductIds: fallbackList(values.paperProductIds),
    scopeOfWorkIds: fallbackList(values.scopeOfWorkIds),
    sowFrequencies: Array.isArray(values.sowFrequencies) ? values.sowFrequencies : [],
  };
};

/**
 * A recurring contract has exactly one end date: the recurrence's "Ends By".
 * Keeping a second, independently editable contract end date invites the two to
 * disagree, and the renewal alert would then fire against the wrong one.
 */
export const resolveContractEndDate = <T extends Record<string, any>>(
  values: T
): T => {
  if (values.recurrence !== "Y") {
    return values;
  }

  return { ...values, endDate: values.recurrenceEndDate ?? null };
};
